import { Card } from '@/components/ui/card';
import { cn, normalizeImageUrl, retryImageOnce } from '@/lib/utils';
import { Content, ContentConfig, ContentType, AdminRole } from '@/types/custom';
import { Music, Volume2, Waves, Heart, Edit, EyeOff } from 'lucide-react';
import { useProfile } from '@/lib/providers';
import { CONTENT_TYPES, IconMap } from '@/lib/constants';

const SHOW_DESCRIPTION: ContentType[] = ['music', 'nature_sound'];
const SHOW_TYPE: ContentType[] = ['meditation', 'binaural'];

interface AudioCardProps {
  content: Content;
  config?: ContentConfig;
  isFavorite?: boolean;
  showIcon?: boolean;
  showLabel?: boolean;
  hideDescription?: boolean; // force hide
  onFavoriteToggle?: (e: React.MouseEvent) => void;
  onClick: () => void;
  requestEdit?: (
    e: React.MouseEvent,
    content: Content,
    config: ContentConfig,
  ) => void;
}

/**
 * Tile for representing a playable audio item.
 */
export default function AudioCard({
  content,
  config,
  isFavorite,
  showIcon,
  showLabel,
  onFavoriteToggle,
  onClick,
  requestEdit,
  hideDescription,
}: AudioCardProps) {
  const { profile, loadingProfile } = useProfile();
  const isAdmin = profile?.role === AdminRole;

  const showDescription =
    SHOW_DESCRIPTION.includes(content.content_type) && !hideDescription;
  const showType = SHOW_TYPE.includes(content.content_type); // somehwat legacy

  const typeLabel = (
    <span className="px-2 py-1 rounded-full bg-white/20">
      {CONTENT_TYPES[content.content_type] || content.content_type}
    </span>
  );

  return (
    <Card
      key={content.id}
      className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
      onClick={onClick}
    >
      <div className="relative aspect-video">
        <img
          src={normalizeImageUrl(content.image_url) || '/images/logotrans.svg'}
          alt={content.title}
          className={cn(
            'w-full h-full object-cover group-hover:scale-105 transition-transform duration-300',
            !content.published && 'opacity-20',
          )}
          loading="lazy"
          onError={retryImageOnce}
        />

        <div className="absolute top-4 right-4 flex gap-2">
          {content.published && isFavorite !== undefined && (
            <button
              onClick={(e) => {
                onFavoriteToggle(e);
                e.stopPropagation();
              }}
              className="p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            >
              <Heart
                className={cn(
                  'w-5 h-5',
                  isFavorite ? 'fill-red-500 stroke-red-500' : 'stroke-white',
                )}
              />
            </button>
          )}
          {isAdmin && requestEdit && (
            <button
              onClick={(e) => {
                requestEdit(e, content, config);
                e.stopPropagation();
              }}
              className="p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            >
              <Edit className="w-5 h-5 stroke-white" />
            </button>
          )}
          {showLabel && <div className="text-white">{typeLabel}</div>}
          {showIcon &&
            (() => {
              const IconComponent = IconMap[content.content_type];
              return <IconComponent className="w-5 h-5 stroke-white" />;
            })()}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white">
          <h3 className="font-semibold text-lg">{content.title}</h3>

          <div className="flex items-center justify-between mt-2 text-sm">
            {showDescription && (
              <span className="text-white/80">{content.description}</span>
            )}

            <div className="flex items-center justify-between gap-2">
              {showType && typeLabel}

              {!content.published && (
                <div
                  className="p-2 rounded-full bg-yellow-500/80 hover:bg-yellow-500 transition-colors"
                  title="Niet gepubliceerd"
                >
                  <EyeOff className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
